import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useCosmos } from '../contexts/CosmosContext';
import { useDataProvider, useNotify } from 'react-admin';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { checkBlackbirdEditorPermission } from 'utility/permissions';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { GetBlackbirdLinkResult } from 'utility/types/dataProvider';

type BlackbirdLinkOptions = { enabled?: boolean };

type BlackbirdErrorResponse = {
  status?: string;
  message?: string;
  details?: Record<string, any>;
  axiosStatus?: number;
  axiosCode?: string;
  axiosMessage?: string;
  axiosMethod?: string;
  axiosUrl?: string;
  axiosBaseUrl?: string;
};

type UseBlackbirdLinkResult = {
  status: UseQueryResult<GetBlackbirdLinkResult, BlackbirdErrorResponse>['status'];
  refetch: UseQueryResult<GetBlackbirdLinkResult, BlackbirdErrorResponse>['refetch'];
  error: BlackbirdErrorResponse | null;
  data?: GetBlackbirdLinkResult['data'];
  configured: boolean;
  isBadRequest: boolean;
  isForbidden: boolean;
  tryBlackbirdLink: (autoOpen?: boolean) => Promise<string | null>;
};

export const useBlackbirdLink = (
  options: BlackbirdLinkOptions = {},
): UseBlackbirdLinkResult => {
  const { sessionConfig, activeSite } = useCosmos();
  const { granted: siteBlackbirdEditor } = checkBlackbirdEditorPermission(sessionConfig);
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const { t } = useTranslation();

  const tBlackbirdUnconfigured = t('blox.editorial.video.blackbird.not_configured');
  const tBadRequest = t('blox.error.bad_request');
  const tNoAccess = t('blox.general.no_access');
  const tUnexpectedError = t('blox.error.unexpectedError');
  const tErrorGeneric = t('blox.general.error');

  const { status, error, data, refetch } = useQuery<
    GetBlackbirdLinkResult,
    BlackbirdErrorResponse
  >({
    queryKey: ['getBlackbirdLink', activeSite],
    queryFn: async () => {
      try {
        const response = await dataProvider.getBlackbirdLink();
        return response;
      } catch (err: any) {
        const errorData: BlackbirdErrorResponse = err?.response?.data || err;
        const errMsg = errorData?.message || errorData?.axiosMessage;
        const errorCode = errorData?.axiosStatus;

        // Log and Notify by specific error cases
        console.error('[useBlackbirdLink]:', errorData);

        if (
          errorCode === 400 &&
          typeof errMsg === 'string' &&
          errMsg?.toLowerCase()?.includes('not configured')
        ) {
          notify(tBlackbirdUnconfigured, { type: 'warning' });
        } else if (errorCode === 400) {
          notify(`${tBadRequest} ${errMsg ?? tUnexpectedError}`, { type: 'error' });
        } else if (errorCode === 403) {
          notify(tNoAccess, { type: 'error' });
        } else {
          notify(`${tErrorGeneric}: ${errorCode}`, { type: 'error' });
        }

        // rethrow for react-query handler
        throw errorData;
      }
    },
    enabled: options?.enabled ?? siteBlackbirdEditor ?? false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 0,
    gcTime: 1000 * 60,
    retry: false,
  });

  const errorStatus = useMemo(() => error?.axiosStatus || null, [error]);
  const url = useMemo(() => data?.data?.url ?? null, [data]);
  const respData = useMemo(() => data?.data ?? undefined, [data]);
  const configured = useMemo(() => {
    return Boolean(siteBlackbirdEditor && url);
  }, [siteBlackbirdEditor, url]);

  // Attempts to fetch the Blackbird link
  // If autoOpen is true, it will open the link in a new tab
  // Returns the URL if successful, or null if not
  const tryBlackbirdLink = useCallback(
    async (autoOpen: boolean = false): Promise<string | null> => {
      try {
        const result = await refetch();
        const url = result.data?.data?.url ?? null;
        if (autoOpen && result?.status === 'success' && url) {
          window.open(url, '_blank');
        }
        return url;
      } catch (error) {
        return null;
      }
    },
    [refetch],
  );

  return {
    status: status,
    data: respData,
    error: error,
    refetch: refetch,
    configured: configured,
    isBadRequest: errorStatus === 400,
    isForbidden: errorStatus === 403,
    tryBlackbirdLink: tryBlackbirdLink,
  };
};
